import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Alert, Box, Stack, Typography } from "@mui/material";

import { useWorkflow } from "../../../../../utils/reactQuery/workflows";
import TransferIcon from "../../../../../styles/svg/iconoir_dot-arrow-right.svg";
import { changeBoardWorkflow } from "../../../../../sevices/apiWorkflows";
import { RFHTextField, RHFSelect } from "../../../../../components/RFH";
import CustomMenuItem from "../../../../../components/Menu/CustomMenuItem";
import { CustomButton } from "../../../../../components/Button/Button";
import useMutate from "../../../../../hooks/useMutate";

function WorkflowForm({ params, onClose }) {
  const [apiError, setApiError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const { isLoading: isWorkflowLoading, workflows } = useWorkflow();
  const [workflowOption, setWorkFlowOption] = useState([]);
  // React Hook Form
  const { handleSubmit, setValue, watch, ...methods } = useForm({
    defaultValues: {
      board_name: params.row.board_name,
      workflow_id: params.row.workflow_id,
    },
  });

  const workflowValue = watch("workflow_id");

  const { mutate } = useMutate(changeBoardWorkflow, ["boards", "boardName"], () => {},
    () => {
      setLoading(false);
      onClose();
    },
    () => {
      setLoading(false);
    });

  const submit = (data) => {
    setApiError("");
    setLoading(true);
    const newData = { ...data };
    delete newData.workflow_id;
    delete newData.board_name;
    delete newData.prev_step;

    mutate({
      board_id: params.row.board_id,
      workflow_id: data.workflow_id,
      data: { workflow_step_exchange_id: newData },
    });
  };

  const formattedWorkflowOptions = useMemo(() => {
    if (!isWorkflowLoading) {
      return workflows.data.data.responseData.map((el) => {
        return { label: el.workflow_name, value: el.workflow_id };
      });
    }
    return [];
  }, [isWorkflowLoading, workflows]);

  useEffect(() => {
    if (!isWorkflowLoading) {
      setWorkFlowOption(formattedWorkflowOptions);
    }
  }, [isWorkflowLoading, formattedWorkflowOptions]);

  const prepareOptions = useCallback(
    (id) => {
      return workflows?.data.data.responseData
        .find((el) => el.workflow_id === id)
        ?.steps?.sort((a, b) => a.step_sequence - b.step_sequence)
        .map((el) => {
          return { label: el.status, value: el.workflow_step_id };
        });
    },
    [workflows]
  );

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(submit)}
        noValidate
        sx={{
          padding: "14px",
        }}
        className="change-workflow-form"
      >
        <Stack spacing={3}>
          {apiError && <Alert severity="error">{apiError}</Alert>}
          <RFHTextField
            id="board_name"
            label={"Board Name"}
            name={"board_name"}
            disabled={true}
            required={true}
            defaultPadding={false}
          />

          <RHFSelect
            label={"Board Workflow"}
            required
            id="workflow_id"
            name="workflow_id"
            className="board-workflow-field"
          >
            {workflowOption.map((option) => (
              <CustomMenuItem
                key={`workflow-option-${option.value}`}
                id={`workflow-option-id-${option.value}`}
                value={option.value}
              >
                {option.label}
              </CustomMenuItem>
            ))}
          </RHFSelect>

          {workflowValue != params?.row?.workflow_id &&
            params?.row?.card_exists && (
              <Box>
                <Typography className="weight-700 size-16 text-color">
                  Task Reassignment Step
                </Typography>
                <Stack spacing={1}>
                  {workflows?.data.data.responseData
                    .find((el) => el.workflow_id == params.row.workflow_id)
                    .steps.map((step, idx) => {
                      setValue(
                        `${step.workflow_step_id}`,
                        prepareOptions(workflowValue)[0]?.value
                      );
                      return (
                        <Box
                          className="board-workflow-change-item"
                          key={`board-workflow-${idx}`}
                        >
                          <RFHTextField
                            sx={{ width: "40%", backgroundColor: "white" }}
                            className="mui-status-field"
                            disabled={true}
                            defaultValue={step?.status || ""}
                            name={"prev_step"}
                          />
                          <img src={TransferIcon} />

                          <RHFSelect
                            sx={{ width: "40%", backgroundColor: "white" }}
                            className="mui-status-field"
                            required
                            id={`${step.workflow_step_id}`}
                            name={`${step.workflow_step_id}`}
                            defaultValue={
                              prepareOptions(workflowValue)[0]?.value
                            }
                          >
                            {prepareOptions(workflowValue).map((option) => (
                              <CustomMenuItem
                                key={`workflow-option-${option.value}`}
                                id={`workflow-option-id-${option.value}`}
                                value={option.value}
                                className="uppercase"
                              >
                                {option.label}
                              </CustomMenuItem>
                            ))}
                          </RHFSelect>
                        </Box>
                      );
                    })}
                </Stack>
              </Box>
            )}
          <Box className="admin-board-workflow-form d-flex">
            <CustomButton
              type="submit"
              className="admin-board-update-button"
              disabled={loading || workflowValue == params.row.workflow_id}
            >
              Update
            </CustomButton>
            <CustomButton
              variant="outlined"
              className="admin-board-cancel-button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </CustomButton>
          </Box>
        </Stack>
      </Box>
    </FormProvider>
  );
}

export default React.memo(WorkflowForm);
